<template>
  <div class="complementos-overlay">
    <div class="complementos-modal">
      <!-- Header -->
      <div class="complementos-modal__header">
        <div>
          <h2 class="complementos-modal__title">
            Asignar complementos
          </h2>
          <p class="complementos-modal__subtitle">
            Selecciona los productos complementarios que acompañan a este producto principal.
          </p>
        </div>

        <button
          type="button"
          class="complementos-modal__close"
          @click="$emit('cerrar')"
        >
          ✕
        </button>
      </div>

      <!-- Info / filtros -->
      <div class="complementos-meta">
        <div class="complementos-meta__left">
          <p class="complementos-meta__count">
            {{ totalComplementos }} complementos disponibles ·
            <span class="highlight">{{ seleccionados.length }} seleccionados</span>
          </p>
        </div>
        <div class="complementos-meta__right">
          <div class="complementos-search">
            <input
              v-model="searchTerm"
              type="text"
              class="complementos-search__input"
              placeholder="Buscar complemento…"
            />
          </div>
        </div>
      </div>

      <!-- Lista -->
      <div class="complementos-list-wrapper">
        <div v-if="loading" class="complementos-empty">
          Cargando complementos…
        </div>

        <div
          v-else-if="filteredComplementos.length === 0"
          class="complementos-empty"
        >
          No se encontraron complementos para mostrar.
        </div>

        <div
          v-else
          class="complementos-list"
        >
          <label
            v-for="producto in filteredComplementos"
            :key="producto.id_producto"
            :class="[
              'complemento-item',
              { 'complemento-item--checked': seleccionados.includes(producto.id_producto) }
            ]"
          >
            <div class="complemento-item__left">
              <input
                type="checkbox"
                class="complemento-item__checkbox"
                :value="producto.id_producto"
                v-model="seleccionados"
              />
            </div>

            <div class="complemento-item__main">
              <p class="complemento-item__name">
                {{ producto.nombre || 'Complemento sin nombre' }}
              </p>
              <p
                v-if="producto.descripcion"
                class="complemento-item__description"
              >
                {{ producto.descripcion }}
              </p>
            </div>
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div class="complementos-footer">
        <button
          type="button"
          class="complementos-btn complementos-btn--ghost"
          @click="$emit('cerrar')"
          :disabled="saving"
        >
          Cancelar
        </button>

        <button
          type="button"
          class="complementos-btn complementos-btn--primary"
          @click="guardarCambios"
          :disabled="saving"
        >
          <span v-if="saving">Guardando…</span>
          <span v-else>Guardar cambios</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AsignarComplementos',
  props: {
    productoId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      complementos: [],
      seleccionados: [],
      existentes: [],
      loading: true,
      saving: false,
      searchTerm: '',
    };
  },
  computed: {
    totalComplementos() {
      return Array.isArray(this.complementos) ? this.complementos.length : 0;
    },
    filteredComplementos() {
      if (!Array.isArray(this.complementos)) return [];
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.complementos;

      return this.complementos.filter((p) =>
        String(p.nombre || '')
          .toLowerCase()
          .includes(term)
      );
    },
  },
  mounted() {
    this.obtenerComplementos();
  },
  methods: {
    async obtenerComplementos() {
      this.loading = true;
      try {
        const [allResponse, actualesResponse] = await Promise.all([
          axios.get('/api/almacen/productos/por-tipo', {
            params: { tipo_producto: 'complemento' },
          }),
          axios.get('/api/almacen/productos/complementos', {
            params: { id_producto: this.productoId },
          }),
        ]);

        this.complementos = Array.isArray(allResponse.data)
          ? allResponse.data
          : [];
        this.existentes = Array.isArray(actualesResponse.data)
          ? actualesResponse.data.map((c) => c.id_producto)
          : [];
        this.seleccionados = [...this.existentes];
      } catch (error) {
        console.error('Error al obtener complementos:', error);
        this.$toast?.error('No se pudieron cargar los complementos.');
      } finally {
        this.loading = false;
      }
    },

    async guardarCambios() {
      if (this.saving) return;
      this.saving = true;

      try {
        const nuevos = this.seleccionados.filter(
          (id) => !this.existentes.includes(id)
        );
        const removidos = this.existentes.filter(
          (id) => !this.seleccionados.includes(id)
        );

        // Asignar nuevos complementos
        if (nuevos.length > 0) {
          await axios.post('/api/almacen/productos/asignar-complementos', {
            id_producto_principal: this.productoId,
            complementos: nuevos,
          });
        }

        // Eliminar relaciones removidas
        for (const idComplemento of removidos) {
          await axios.post('/api/almacen/productos/eliminar-complemento', {
            id_producto_principal: this.productoId,
            id_producto_complemento: idComplemento,
          });
        }

        // Si todo salió bien: toast de éxito y cerrar modal
        this.$toast?.success('Complementos actualizados correctamente.');
        this.$emit('cerrar');
      } catch (error) {
        console.error('Error al guardar cambios de complementos:', error);
        // Error: mostramos toast, pero NO cerramos el modal
        this.$toast?.error('Ocurrió un error al guardar los complementos.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.complementos-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.3),
      rgba(15, 23, 42, 0.98)
    );
  backdrop-filter: blur(18px);
}

/* Tarjeta */
.complementos-modal {
  width: 100%;
  max-width: 720px;
  border-radius: 1.6rem;
  padding: 1.3rem 1.3rem 1.1rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.97)
    );
  border: 1px solid rgba(148, 163, 184, 0.7);
  box-shadow:
    0 22px 60px rgba(15, 23, 42, 0.95),
    0 0 0 1px rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
}

/* Header */
.complementos-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
}

.complementos-modal__title {
  font-size: 1rem;
  font-weight: 600;
  color: #f9fafb;
}

.complementos-modal__subtitle {
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

.complementos-modal__close {
  border: none;
  background: transparent;
  color: #cbd5f5;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.2rem;
}

/* Meta / buscador */
.complementos-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.7rem;
  font-size: 0.78rem;
}

.complementos-meta__count {
  color: #cbd5f5;
}

.complementos-meta__count .highlight {
  color: #a5b4fc;
}

.complementos-meta__right {
  flex-shrink: 0;
}

.complementos-search {
  min-width: 180px;
}

.complementos-search__input {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.9);
  background: rgba(15, 23, 42, 0.94);
  padding: 0.28rem 0.65rem;
  font-size: 0.78rem;
  color: #e5e7eb;
  outline: none;
}

.complementos-search__input::placeholder {
  color: #6b7280;
}

/* Lista */
.complementos-list-wrapper {
  border-radius: 1.2rem;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(15, 23, 42, 0.94);
  padding: 0.55rem 0.55rem 0.5rem;
  min-height: 100px;
}

.complementos-list {
  max-height: 260px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.7) transparent;
}

/* WebKit scrollbars */
.complementos-list::-webkit-scrollbar {
  width: 4px;
}

.complementos-list::-webkit-scrollbar-track {
  background: transparent;
}

.complementos-list::-webkit-scrollbar-thumb {
  background: linear-gradient(
    180deg,
    rgba(148, 163, 184, 0.9),
    rgba(129, 140, 248, 0.9)
  );
  border-radius: 999px;
}

/* Item */
.complemento-item {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.45rem 0.55rem;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.5);
  cursor: pointer;
  transition:
    background 0.14s ease,
    border-color 0.14s ease,
    box-shadow 0.16s ease;
}

.complemento-item:hover {
  background: rgba(15, 23, 42, 0.98);
  border-color: rgba(148, 163, 184, 0.9);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.95);
}

/* Estado seleccionado */
.complemento-item--checked {
  background: rgba(15, 23, 42, 0.98);
  border-color: rgba(129, 140, 248, 0.95);
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.98);
}

.complemento-item__left {
  padding-top: 0.1rem;
}

/* Checkbox custom */
.complemento-item__checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 0.6rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: radial-gradient(
      circle at 30% 30%,
      rgba(148, 163, 184, 0.4),
      rgba(15, 23, 42, 0.98)
    );
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.9),
    0 6px 16px rgba(15, 23, 42, 0.9);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.14s ease,
    border-color 0.14s ease,
    box-shadow 0.16s ease,
    transform 0.08s ease;
}

.complemento-item__checkbox:hover {
  border-color: rgba(129, 140, 248, 0.95);
}

.complemento-item__checkbox:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 1px rgba(96, 165, 250, 0.9),
    0 0 0 4px rgba(37, 99, 235, 0.35);
}

.complemento-item__checkbox::after {
  content: '';
  width: 9px;
  height: 9px;
  border-radius: 0.4rem;
  transform: scale(0);
  transition: transform 0.12s ease;
}

.complemento-item__checkbox:checked {
  border-color: rgba(129, 140, 248, 1);
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.98),
    rgba(37, 99, 235, 0.98)
  );
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.9),
    0 10px 24px rgba(15, 23, 42, 0.95);
}

.complemento-item__checkbox:checked::after {
  background: #e5e7eb;
  transform: scale(1);
}

.complemento-item__main {
  flex: 1;
  min-width: 0;
}

.complemento-item__name {
  font-size: 0.83rem;
  color: #e5e7eb;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.complemento-item__description {
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

/* Estados vacíos / cargando */
.complementos-empty {
  font-size: 0.8rem;
  color: #e5e7eb;
  padding: 0.35rem 0.25rem;
}

/* Footer */
.complementos-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.45rem;
  margin-top: 0.8rem;
}

.complementos-btn {
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.8rem;
  padding: 0.35rem 0.9rem;
  cursor: pointer;
  min-width: 90px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.18s ease,
    transform 0.08s ease;
}

.complementos-btn--ghost {
  background: rgba(15, 23, 42, 0.88);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.complementos-btn--ghost:hover:not(:disabled) {
  background: rgba(15, 23, 42, 1);
  border-color: rgba(148, 163, 184, 0.95);
}

.complementos-btn--primary {
  background: #e5e7eb;
  color: #020617;
}

.complementos-btn--primary:hover:not(:disabled) {
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.9);
}

.complementos-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Responsive */
@media (max-width: 640px) {
  .complementos-modal {
    padding: 1.1rem 1rem 1rem;
    border-radius: 1.35rem;
  }

  .complementos-meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .complementos-meta__right {
    width: 100%;
  }

  .complementos-search {
    width: 100%;
  }
}
</style>
